import { useCallback } from 'react'

import {
  type QueryClient,
  type QueryKey,
  UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { uniqBy } from 'es-toolkit'
import { produce } from 'immer'

import { useAuthInfo } from '@sweetauth/core'

import {
  type ChatSessionListQueryKey,
  type ChatSessionListQueryKeyArgs,
  type ChatSessionQueryKeyArgs,
  chatSessionListQueryKeys,
  chatSessionQueryKeys,
  simpleChatSessionQueryKeys,
} from '@/components/chat-v2/hooks/chatSessionQueryKeys'
import { isSystemReminderPart } from '@/components/chat-v2/lib/contentConverters'
import { fetchOpportunityChatSessionSync } from '@/components/chat-v2/lib/opportunitySync'
import { fetchOrganizationChatSessionSync } from '@/components/chat-v2/lib/organizationSync'
import { fetchPursuitDocumentChatSessionSync } from '@/components/chat-v2/lib/pursuitDocumentSync'
import { fetchPursuitChatSessionSync } from '@/components/chat-v2/lib/pursuitSync'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import {
  AgentName,
  OpportunityChatSession,
  OrganizationChatSession,
  PursuitChatSession,
  PursuitDocumentChatSession,
  UserContent,
} from '@/types/chat-v2'
import { UUID } from '@/types/Id'
import { OpportunityType } from '@/types/Opportunity'
import type { AccessGrant } from '@/types/Permission'

const DEFAULT_MODEL = 'sweetspot/v1/auto'

export interface ChatSessionUserGrant {
  user_id: UUID
  grant: AccessGrant
}

export interface ChatSessionCurrentUserAccess {
  source: 'owner' | 'shared' | 'inherited'
  grant: AccessGrant | null
}

export interface ChatSessionUpdate {
  title?: string | null
  /** @deprecated Use shared_with_user_grants so read/write grants are preserved. */
  shared_with_user_ids?: UUID[]
  shared_with_user_grants?: ChatSessionUserGrant[]
  current_run_id?: UUID
}

export interface BulkChatSessionsDelete {
  ids: UUID[]
}

interface BaseChatSessionListItem {
  id: UUID
  created_at: string
  created_by: UUID
  last_modified: string
  pinned_at?: string | null
  shared_user_ids: UUID[]
  current_user_access?: ChatSessionCurrentUserAccess | null
  /** @deprecated Compatibility-only wire alias. Use shared_user_ids. */
  shared_with_user_ids?: UUID[]
  shared_with_user_grants?: ChatSessionUserGrant[]
  title: string | null
  temporary_title_preview?: string | null
}

export interface OrganizationChatSessionListItem extends BaseChatSessionListItem {
  initial_agent_name: 'organization_chat'
  organization_id: UUID
}

export interface OpportunityChatSessionListItem extends BaseChatSessionListItem {
  initial_agent_name: 'opportunity_chat'
  organization_id: UUID
}

export interface SearchChatSessionListItem extends BaseChatSessionListItem {
  initial_agent_name: 'search_chat'
  organization_id: UUID
}

export interface PursuitChatSessionListItem extends BaseChatSessionListItem {
  initial_agent_name: 'pursuit_chat'
  organization_id: UUID
  pursuit_id: UUID
}

export interface PursuitDocumentChatSessionListItem extends BaseChatSessionListItem {
  initial_agent_name: 'pursuit_document_chat'
  organization_id: UUID
  pursuit_id: UUID
  document_id: UUID
}

export interface FormChatSessionListItem extends BaseChatSessionListItem {
  initial_agent_name: 'form_chat'
}

export interface SpreadsheetChatSessionListItem extends BaseChatSessionListItem {
  initial_agent_name: 'spreadsheet_chat'
}

export type ChatSessionListItem =
  | OrganizationChatSessionListItem
  | OpportunityChatSessionListItem
  | SearchChatSessionListItem
  | PursuitChatSessionListItem
  | PursuitDocumentChatSessionListItem
  | FormChatSessionListItem
  | SpreadsheetChatSessionListItem

/**
 * Every agent a session can belong to. Wider than `AgentName`: form and
 * spreadsheet chats own sessions but have no chat page of their own.
 */
export type ChatSessionAgentName = ChatSessionListItem['initial_agent_name']

export const isPinnableChatSessionListItem = (
  session: ChatSessionListItem,
): boolean => {
  switch (session.initial_agent_name) {
    case 'organization_chat':
    case 'opportunity_chat':
    case 'pursuit_chat':
      return true
    case 'search_chat':
    case 'pursuit_document_chat':
    case 'form_chat':
    case 'spreadsheet_chat':
      return false
    default: {
      const exhaustiveCheck: never = session
      return exhaustiveCheck
    }
  }
}

export interface ChatSessionListResponse {
  sessions: ChatSessionListItem[]
  count: number
  has_more: boolean
}

export const getSharedUserIds = (session: {
  shared_user_ids?: UUID[]
  shared_with_user_ids?: UUID[]
}): UUID[] => session.shared_user_ids ?? session.shared_with_user_ids ?? []

export type FetchableChatSession =
  | OpportunityChatSession
  | OrganizationChatSession
  | PursuitChatSession
  | PursuitDocumentChatSession

export type FetchableAgentName = FetchableChatSession['initial_agent_name']

export const isFetchableAgentName = (
  agentName: ChatSessionAgentName,
): agentName is FetchableAgentName => {
  switch (agentName) {
    case 'opportunity_chat':
    case 'organization_chat':
    case 'pursuit_chat':
    case 'pursuit_document_chat':
      return true
    case 'search_chat':
    case 'form_chat':
    case 'spreadsheet_chat':
      return false
    default:
      const exhaustiveCheck: never = agentName
      return false
  }
}

export interface FetchableChatSessionQueryKeyArgs extends Omit<
  ChatSessionQueryKeyArgs,
  'agentName'
> {
  agentName: FetchableAgentName
}

interface UseFetchableChatSessionArgs<T> {
  agentName: FetchableAgentName
  orgId: UUID
  sessionId: UUID | ''
  enabled?: boolean
  select?: (data: FetchableChatSession) => T
}

const fetchChatSession = async (
  queryClient: QueryClient,
  args: FetchableChatSessionQueryKeyArgs,
  accessToken: string,
): Promise<FetchableChatSession> => {
  let session: FetchableChatSession

  switch (args.agentName) {
    case 'opportunity_chat':
      session = await fetchOpportunityChatSessionSync(
        queryClient,
        args,
        accessToken,
        'fetchable_query',
      )
      break
    case 'organization_chat':
      session = await fetchOrganizationChatSessionSync(
        queryClient,
        args,
        accessToken,
        'fetchable_query',
      )
      break
    case 'pursuit_chat':
      session = await fetchPursuitChatSessionSync(
        queryClient,
        args,
        accessToken,
        'fetchable_query',
      )
      break
    case 'pursuit_document_chat':
      session = await fetchPursuitDocumentChatSessionSync(
        queryClient,
        args,
        accessToken,
        'fetchable_query',
      )
      break
    default: {
      const exhaustiveCheck: never = args.agentName
      return exhaustiveCheck
    }
  }

  syncChatSessionListItemCaches(queryClient, session)
  return session
}

const getFetchableChatSessionQueryOptions = (
  queryClient: QueryClient,
  args: FetchableChatSessionQueryKeyArgs,
  accessToken: string,
) => {
  return {
    queryKey: chatSessionQueryKeys.session(args),
    queryFn: () => fetchChatSession(queryClient, args, accessToken),
  } as const
}

export const ensureChatSession = (
  queryClient: QueryClient,
  args: FetchableChatSessionQueryKeyArgs,
  accessToken: string,
) => {
  return queryClient.ensureQueryData<FetchableChatSession>({
    ...getFetchableChatSessionQueryOptions(queryClient, args, accessToken),
    staleTime: Infinity,
  })
}

export const refetchChatSession = (
  queryClient: QueryClient,
  args: FetchableChatSessionQueryKeyArgs,
  accessToken: string,
) => {
  return queryClient.fetchQuery<FetchableChatSession>({
    ...getFetchableChatSessionQueryOptions(queryClient, args, accessToken),
    staleTime: 0,
  })
}

export const useFetchableChatSession = <T = FetchableChatSession>(
  args: UseFetchableChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (data: FetchableChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    ...getFetchableChatSessionQueryOptions(
      queryClient,
      {
        agentName: args.agentName,
        orgId: args.orgId,
        sessionId: args.sessionId,
      },
      accessToken,
    ),
    select: selectFn,
    enabled: args.enabled ?? !!args.sessionId,
    staleTime: Infinity,
  })
}

// region: Session List Fetch Functions

interface FetchOpportunityChatSessionListArgs {
  orgId: UUID
}

const fetchOpportunityChatSessionList = async (
  args: FetchOpportunityChatSessionListArgs,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/opportunity/sessions?organization_id=${args.orgId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

interface FetchOrganizationChatSessionListArgs {
  orgId: UUID
}

const fetchOrganizationChatSessionList = async (
  args: FetchOrganizationChatSessionListArgs,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/organization/${args.orgId}/sessions`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

interface FetchPursuitChatSessionListArgs {
  pursuitId: UUID | null
}

const fetchPursuitChatSessionList = async (
  args: FetchPursuitChatSessionListArgs,
  accessToken: string,
) => {
  // Without pursuit_id the endpoint returns every pursuit chat the viewer
  // created or was granted — one request instead of one per pursuit.
  const url = args.pursuitId
    ? `${API_URL}/api/v1/chat/pursuit/sessions?pursuit_id=${args.pursuitId}`
    : `${API_URL}/api/v1/chat/pursuit/sessions`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

interface FetchPursuitDocumentChatSessionListArgs {
  pursuitId: UUID
  documentId: UUID
}

const fetchPursuitDocumentChatSessionList = async (
  args: FetchPursuitDocumentChatSessionListArgs,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions?pursuit_id=${args.pursuitId}&document_id=${args.documentId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

const fetchChatSessionList = async (
  args: ChatSessionListQueryKeyArgs,
  accessToken: string,
) => {
  switch (args.agentName) {
    case 'opportunity_chat':
      return fetchOpportunityChatSessionList(args, accessToken)
    case 'organization_chat':
      return fetchOrganizationChatSessionList(args, accessToken)
    case 'pursuit_chat':
      return fetchPursuitChatSessionList(args, accessToken)
    case 'pursuit_document_chat':
      return fetchPursuitDocumentChatSessionList(args, accessToken)
    default:
      const exhaustiveCheck: never = args
      return exhaustiveCheck
  }
}

// endregion: Session List Fetch Functions

// region: Opportunity Chats

interface UseOpportunityChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  enabled?: boolean
  select?: (data: OpportunityChatSession) => T
}

export const useOpportunityChatSession = <T = OpportunityChatSession>(
  args: UseOpportunityChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (data: OpportunityChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'opportunity_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: async () => {
      const session = await fetchOpportunityChatSessionSync(
        queryClient,
        { orgId: args.orgId, sessionId: args.sessionId },
        accessToken,
        'initial_query',
      )
      syncChatSessionListItemCaches(queryClient, session)
      return session
    },
    select: selectFn,
    enabled: args.enabled ?? !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreateOpportunityChatSessionBody {
  organization_id: UUID
  opportunity_id: UUID
  opportunity_type: OpportunityType
  title?: string | null
  model_name?: string
}

export const useCreateOpportunityChatSession = () => {
  // use regular auth info here since the user might not be logged in
  // when this hook is used
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (args: CreateOpportunityChatSessionBody) =>
      createOpportunityChatSession(args, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'opportunity_chat',
          orgId: variables.organization_id,
          sessionId: data.id,
        }),
        data,
      )

      syncChatSessionListItemCaches(queryClient, data)

      return Promise.all(
        chatSessionListQueryKeys
          .forScope({
            agentName: 'opportunity_chat',
            orgId: variables.organization_id,
          })
          .map((queryKey) =>
            queryClient.invalidateQueries({ queryKey, exact: true }),
          ),
      )
    },
  })
}

interface UseUpdateOpportunityChatSessionArgs {
  orgId: UUID
  sessionId: UUID
}

export const useUpdateOpportunityChatSession = (
  args: UseUpdateOpportunityChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updateOpportunityChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'opportunity_chat',
          orgId: args.orgId,
          sessionId: args.sessionId,
        }),
      })

      queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.organization({
          orgId: args.orgId,
        }),
      })

      return Promise.all(
        chatSessionListQueryKeys
          .forScope({ agentName: 'opportunity_chat', orgId: args.orgId })
          .map((queryKey) =>
            queryClient.invalidateQueries({ queryKey, exact: true }),
          ),
      )
    },
  })
}

interface UseOpportunityChatSessionListArgs {
  orgId: UUID
}

export const useOpportunityChatSessionList = (
  args: UseOpportunityChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.organization({ orgId: args.orgId }),
    queryFn: () => fetchOpportunityChatSessionList(args, accessToken),
    staleTime: Infinity,
  })
}

export const createOpportunityChatSession = async (
  args: CreateOpportunityChatSessionBody,
  accessToken: string | null | undefined,
) => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/opportunity/sessions`

  if (!args.model_name) {
    args.model_name = DEFAULT_MODEL
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(args),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as OpportunityChatSession
}

const updateOpportunityChatSession = async (
  args: UseUpdateOpportunityChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/opportunity/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Opportunity Chats

// region: Organization Chats

interface UseOrganizationChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  enabled?: boolean
  select?: (data: OrganizationChatSession) => T
}

export const useOrganizationChatSession = <T = OrganizationChatSession>(
  args: UseOrganizationChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (data: OrganizationChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'organization_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: async () => {
      const session = await fetchOrganizationChatSessionSync(
        queryClient,
        args,
        accessToken,
        'initial_query',
      )
      syncChatSessionListItemCaches(queryClient, session)
      return session
    },
    select: selectFn,
    enabled: args.enabled ?? !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreateOrganizationChatSessionBody {
  organization_id: UUID
  model_name?: string
  title?: string | null
}

export const useCreateOrganizationChatSession = () => {
  // use regular auth info here since the user might not be logged in
  // when this hook is used
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (args: CreateOrganizationChatSessionBody) =>
      createOrganizationChatSession(args, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'organization_chat',
          orgId: variables.organization_id,
          sessionId: data.id,
        }),
        data,
      )

      syncChatSessionListItemCaches(queryClient, data)

      return Promise.all(
        chatSessionListQueryKeys
          .forScope({
            agentName: 'organization_chat',
            orgId: variables.organization_id,
          })
          .map((queryKey) =>
            queryClient.invalidateQueries({ queryKey, exact: true }),
          ),
      )
    },
  })
}

interface UseUpdateOrganizationChatSessionArgs {
  organizationId: UUID
  sessionId: UUID
}

export const useUpdateOrganizationChatSession = (
  args: UseUpdateOrganizationChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updateOrganizationChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'organization_chat',
          orgId: args.organizationId,
          sessionId: args.sessionId,
        }),
      })

      return Promise.all(
        chatSessionListQueryKeys
          .forScope({
            agentName: 'organization_chat',
            orgId: args.organizationId,
          })
          .map((queryKey) =>
            queryClient.invalidateQueries({ queryKey, exact: true }),
          ),
      )
    },
  })
}

interface UseOrganizationChatSessionListArgs {
  organizationId: UUID
}

export const useOrganizationChatSessionList = (
  args: UseOrganizationChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.agent({
      agentName: 'organization_chat',
      orgId: args.organizationId,
    }),
    queryFn: () =>
      fetchOrganizationChatSessionList(
        { orgId: args.organizationId },
        accessToken,
      ),
    staleTime: Infinity,
  })
}

export const createOrganizationChatSession = async (
  args: CreateOrganizationChatSessionBody,
  accessToken: string | null | undefined,
) => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/organization/${args.organization_id}/sessions`

  if (!args.model_name) {
    args.model_name = DEFAULT_MODEL
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(args),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as OrganizationChatSession
}

const updateOrganizationChatSession = async (
  args: UseUpdateOrganizationChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/organization/${args.organizationId}/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Organization Chats

// region: Pursuit Chats

interface UsePursuitChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  enabled?: boolean
  select?: (data: PursuitChatSession) => T
}

export const usePursuitChatSession = <T = PursuitChatSession>(
  args: UsePursuitChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (data: PursuitChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'pursuit_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: async () => {
      const session = await fetchPursuitChatSessionSync(
        queryClient,
        { orgId: args.orgId, sessionId: args.sessionId },
        accessToken,
        'initial_query',
      )
      syncChatSessionListItemCaches(queryClient, session)
      return session
    },
    select: selectFn,
    enabled: args.enabled ?? !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreatePursuitChatSessionBody {
  pursuitId: UUID
  organizationId: UUID
  model_name?: string
  title?: string | null
}

export const useCreatePursuitChatSession = () => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (args: CreatePursuitChatSessionBody) =>
      createPursuitChatSession(args, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'pursuit_chat',
          orgId: data.organization_id,
          sessionId: data.id,
        }),
        data,
      )

      syncChatSessionListItemCaches(queryClient, data)

      // forScope includes the aggregate all-pursuits list alongside the
      // specific one, so the org sidebar's folders pick the session up too.
      return Promise.all(
        chatSessionListQueryKeys
          .forScope({
            agentName: 'pursuit_chat',
            pursuitId: variables.pursuitId,
          })
          .map((queryKey) =>
            queryClient.invalidateQueries({ queryKey, exact: true }),
          ),
      )
    },
  })
}

interface UseUpdatePursuitChatSessionArgs {
  orgId: UUID
  sessionId: UUID
  pursuitId?: UUID
}

export const useUpdatePursuitChatSession = (
  args: UseUpdatePursuitChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updatePursuitChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'pursuit_chat',
          orgId: args.orgId,
          sessionId: args.sessionId,
        }),
      })

      if (args.pursuitId) {
        // forScope includes the aggregate all-pursuits list alongside the
        // specific one, so org-sidebar folders see the update too.
        return Promise.all(
          chatSessionListQueryKeys
            .forScope({ agentName: 'pursuit_chat', pursuitId: args.pursuitId })
            .map((queryKey) =>
              queryClient.invalidateQueries({ queryKey, exact: true }),
            ),
        )
      }
    },
  })
}

interface UsePursuitChatSessionListArgs {
  orgId: UUID
  pursuitId: UUID
}

export const usePursuitChatSessionList = (
  args: UsePursuitChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.pursuit({
      pursuitId: args.pursuitId,
    }),
    queryFn: () => fetchPursuitChatSessionList(args, accessToken),
    staleTime: Infinity,
  })
}

interface CreatePursuitChatSessionRequest {
  pursuit_id: UUID
  organization_id: UUID
  model_name?: string
  title?: string | null
}

export const createPursuitChatSession = async (
  args: CreatePursuitChatSessionBody,
  accessToken: string | null | undefined,
) => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/pursuit/sessions`

  const body: CreatePursuitChatSessionRequest = {
    pursuit_id: args.pursuitId,
    organization_id: args.organizationId,
    model_name: args.model_name ?? DEFAULT_MODEL,
    title: args.title,
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as PursuitChatSession
}

const updatePursuitChatSession = async (
  args: UseUpdatePursuitChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/pursuit/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Pursuit Chats

// region: Pursuit Document Chats

interface UsePursuitDocumentChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  enabled?: boolean
  select?: (data: PursuitDocumentChatSession) => T
}

export const usePursuitDocumentChatSession = <T = PursuitDocumentChatSession>(
  args: UsePursuitDocumentChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (data: PursuitDocumentChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'pursuit_document_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: async () => {
      const session = await fetchPursuitDocumentChatSessionSync(
        queryClient,
        args,
        accessToken,
        'initial_query',
      )
      syncChatSessionListItemCaches(queryClient, session)
      return session
    },
    select: selectFn,
    enabled: args.enabled ?? !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreatePursuitDocumentChatSessionBody {
  pursuitId: UUID
  documentId: UUID
  organizationId: UUID
  model_name?: string
  title?: string | null
}

export const useCreatePursuitDocumentChatSession = () => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: async (args: CreatePursuitDocumentChatSessionBody) => {
      const createdSession = await createPursuitDocumentChatSession(
        args,
        accessToken,
      )
      if (!accessToken) {
        throw new Error('Access token is required')
      }

      return await fetchPursuitDocumentChatSessionSync(
        queryClient,
        {
          orgId: args.organizationId,
          sessionId: createdSession.id,
        },
        accessToken,
        'create_mutation',
      )
    },
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'pursuit_document_chat',
          orgId: data.organization_id,
          sessionId: data.id,
        }),
        data,
      )

      syncChatSessionListItemCaches(queryClient, data)

      return Promise.all(
        chatSessionListQueryKeys
          .forScope({
            agentName: 'pursuit_document_chat',
            pursuitId: variables.pursuitId,
            documentId: variables.documentId,
          })
          .map((queryKey) =>
            queryClient.invalidateQueries({ queryKey, exact: true }),
          ),
      )
    },
  })
}

interface UseUpdatePursuitDocumentChatSessionArgs {
  orgId: UUID
  sessionId: UUID
  pursuitId?: UUID
  documentId?: UUID
}

export const useUpdatePursuitDocumentChatSession = (
  args: UseUpdatePursuitDocumentChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updatePursuitDocumentChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'pursuit_document_chat',
          orgId: args.orgId,
          sessionId: args.sessionId,
        }),
      })

      if (args.pursuitId && args.documentId) {
        return Promise.all(
          chatSessionListQueryKeys
            .forScope({
              agentName: 'pursuit_document_chat',
              pursuitId: args.pursuitId,
              documentId: args.documentId,
            })
            .map((queryKey) =>
              queryClient.invalidateQueries({ queryKey, exact: true }),
            ),
        )
      }
    },
  })
}

interface UsePursuitDocumentChatSessionListArgs {
  orgId: UUID
  pursuitId: UUID
  documentId: UUID
}

export const usePursuitDocumentChatSessionList = (
  args: UsePursuitDocumentChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.pursuitDocument({
      pursuitId: args.pursuitId,
      documentId: args.documentId,
    }),
    queryFn: () => fetchPursuitDocumentChatSessionList(args, accessToken),
    staleTime: Infinity,
  })
}

interface CreatePursuitDocumentChatSessionRequest {
  pursuit_id: UUID
  document_id: UUID
  organization_id: UUID
  model_name?: string
  title?: string | null
}

interface CreatePursuitDocumentChatSessionResponse {
  id: UUID
}

export const createPursuitDocumentChatSession = async (
  args: CreatePursuitDocumentChatSessionBody,
  accessToken: string | null | undefined,
): Promise<CreatePursuitDocumentChatSessionResponse> => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions`

  const body: CreatePursuitDocumentChatSessionRequest = {
    pursuit_id: args.pursuitId,
    document_id: args.documentId,
    organization_id: args.organizationId,
    model_name: args.model_name ?? DEFAULT_MODEL,
    title: args.title,
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as CreatePursuitDocumentChatSessionResponse
}

const updatePursuitDocumentChatSession = async (
  args: UseUpdatePursuitDocumentChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Pursuit Document Chats

// endregion: Pursuit Chats

// region: Simple Chats

interface UseSimpleChatSessionArgs<T> {
  sessionId: UUID | ''
  select?: (data: ChatSessionListItem) => T
  staleTime?: number
}

export const useSimpleChatSession = <T = ChatSessionListItem>(
  args: UseSimpleChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (data: ChatSessionListItem) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: simpleChatSessionQueryKeys.session(args),
    queryFn: async () => {
      const data = await fetchSimpleChatSession(args, accessToken)
      return mergeCachedChatSessionListItemFields(queryClient, data)
    },
    staleTime: args.staleTime ?? Infinity,
    enabled: !!args.sessionId,
    select: selectFn,
    // The sidebar list query is observed for the whole layout lifetime, so it
    // outlives the observer-less per-session seeds `useChatSessions` writes
    // (default gcTime evicts those after 5 idle minutes). Falling back to the
    // list entry keeps the header title and session menu rendered during the
    // fetch instead of flashing a skeleton on every session switch.
    placeholderData: () =>
      findChatSessionInListCaches(queryClient, args.sessionId),
  })
}

interface UseChatSessionAuthorArgs {
  sessionId: UUID | undefined
}

const selectChatSessionAuthor = (data: ChatSessionListItem) => {
  return data.created_by
}

export const useChatSessionAuthor = (args: UseChatSessionAuthorArgs) => {
  return useSimpleChatSession({
    sessionId: args.sessionId ?? '',
    select: selectChatSessionAuthor,
  })
}

interface UseChatSessionIsSharedArgs {
  sessionId: UUID | undefined
}

export const useChatSessionIsShared = (args: UseChatSessionIsSharedArgs) => {
  const { user } = useAuthInfo()

  const selectChatSessionIsShared = useCallback(
    (data: ChatSessionListItem) => {
      if (user === undefined || user === null) {
        return false
      }
      return data.created_by !== user.userId
    },
    [user?.userId],
  )

  return useSimpleChatSession({
    sessionId: args.sessionId ?? '',
    select: selectChatSessionIsShared,
  })
}

interface FetchSimpleChatSessionArgs {
  sessionId: UUID | ''
}

const fetchSimpleChatSession = async (
  args: FetchSimpleChatSessionArgs,
  accessToken: string,
) => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }

  return (await res.json()) as ChatSessionListItem
}

export interface UseUpdateSimpleChatSessionArgs {
  sessionId: UUID
  /**
   * The exact list keys holding this session, derived from the session the
   * caller already has via `chatSessionListQueryKeys.forSession`/`forScope`.
   * Undefined (session not loaded yet) falls back to marking every session
   * list stale.
   */
  listQueryKeys?: ChatSessionListQueryKey[]
}

interface ChatSessionShareStateUpdate {
  shared_with_user_grants: ChatSessionUserGrant[]
}

interface ChatSessionPinStateUpdate {
  pinned: boolean
}

/**
 * The aggregate all-pursuits list (`pursuitId: null`) returns pursuit chats
 * from every org the viewer can access. Surfaces scoped to one org must drop
 * cross-org pursuit chats before any downstream use.
 */
export const isChatSessionInOrg = (
  session: ChatSessionListItem,
  orgId: UUID | '',
): boolean =>
  session.initial_agent_name !== 'pursuit_chat' ||
  session.organization_id === orgId

/**
 * Marks the given session lists stale (exact matches only). `undefined`
 * means the caller could not derive the affected lists — e.g. the session
 * had not loaded yet — so every session list is marked stale instead.
 */
const invalidateChatSessionListQueries = (
  queryClient: QueryClient,
  listQueryKeys: ChatSessionListQueryKey[] | undefined,
) =>
  listQueryKeys === undefined
    ? queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.all(),
        exact: false,
      })
    : Promise.all(
        uniqBy(listQueryKeys, (queryKey) => queryKey.join('|')).map(
          (queryKey) =>
            queryClient.invalidateQueries({ queryKey, exact: true }),
        ),
      )

export const useUpdateSimpleChatSession = (
  args: UseUpdateSimpleChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updateSimpleChatSession({ sessionId: args.sessionId }, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: simpleChatSessionQueryKeys.session({
          sessionId: args.sessionId,
        }),
      })

      return invalidateChatSessionListQueries(queryClient, args.listQueryKeys)
    },
  })
}

export const useUpdateSimpleChatSessionShareState = (
  args: UseUpdateSimpleChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: ChatSessionShareStateUpdate) =>
      updateSimpleChatSession({ sessionId: args.sessionId }, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: simpleChatSessionQueryKeys.session({
          sessionId: args.sessionId,
        }),
      })
      void invalidateChatSessionListQueries(queryClient, args.listQueryKeys)
    },
  })
}

const updateCachedChatSessionPinState = (
  queryClient: QueryClient,
  args: {
    sessionId: UUID
    pinnedAt: string | null
    listQueryKeys: ChatSessionListQueryKey[]
  },
) => {
  queryClient.setQueryData<ChatSessionListItem>(
    simpleChatSessionQueryKeys.session({ sessionId: args.sessionId }),
    (current) =>
      current
        ? {
            ...current,
            pinned_at: args.pinnedAt,
          }
        : current,
  )

  for (const queryKey of args.listQueryKeys) {
    queryClient.setQueryData<ChatSessionListResponse>(
      queryKey,
      produce((draft) => {
        if (!draft) {
          return undefined
        }

        const session = draft.sessions.find(
          (current) => current.id === args.sessionId,
        )
        if (session) {
          session.pinned_at = args.pinnedAt
        }
      }),
    )
  }
}

export const useUpdateChatSessionPinState = (
  args: UseUpdateSimpleChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: ChatSessionPinStateUpdate) =>
      updateChatSessionPinState(
        { sessionId: args.sessionId },
        req,
        accessToken,
      ),
    onMutate: async (req) => {
      const sessionQueryKey = simpleChatSessionQueryKeys.session({
        sessionId: args.sessionId,
      })
      const listQueryKeys = args.listQueryKeys ?? []

      await Promise.all(
        [sessionQueryKey, ...listQueryKeys].map((queryKey) =>
          queryClient.cancelQueries({ queryKey, exact: true }),
        ),
      )

      const previousSession =
        queryClient.getQueryData<ChatSessionListItem>(sessionQueryKey)
      const previousLists = listQueryKeys.map(
        (queryKey) =>
          [
            queryKey,
            queryClient.getQueryData<ChatSessionListResponse>(queryKey),
          ] as const,
      )

      updateCachedChatSessionPinState(queryClient, {
        sessionId: args.sessionId,
        pinnedAt: req.pinned ? new Date().toISOString() : null,
        listQueryKeys,
      })

      return { sessionQueryKey, previousSession, previousLists }
    },
    onError: (error, _req, context) => {
      if (context) {
        queryClient.setQueryData(
          context.sessionQueryKey,
          context.previousSession,
        )
        for (const [queryKey, previousList] of context.previousLists) {
          queryClient.setQueryData(queryKey, previousList)
        }
      }
      handleError(error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: simpleChatSessionQueryKeys.session({
          sessionId: args.sessionId,
        }),
      })
      void invalidateChatSessionListQueries(queryClient, args.listQueryKeys)
    },
  })
}

const updateSimpleChatSession = async (
  args: { sessionId: UUID },
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

const updateChatSessionPinState = async (
  args: { sessionId: UUID },
  req: ChatSessionPinStateUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/sessions/${args.sessionId}/pin`

  const res: Response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

// endregion: Simple Chats

// region: User Chats

export interface UseChatSessionsArgs {
  queryKeys: ChatSessionListQueryKeyArgs[]
  select?: (data: ChatSessionListItem[]) => ChatSessionListItem[]
  /**
   * Default Infinity: session lists are kept fresh by targeted invalidation
   * from the chat runtime and session CRUD. Pursuit mutations invalidate only
   * the aggregate list and the affected pursuit's specific list.
   */
  staleTime?: number
}

interface FetchedChatSessionList extends ChatSessionListResponse {
  /** Local request-start time; absent from manually seeded cache entries. */
  requestedAt?: number
}

const combineChatSessionListResponses = (
  responses: UseQueryResult<FetchedChatSessionList, Error>[],
) => {
  return {
    data: responses.flatMap((response) => response.data?.sessions ?? []),
    // A combined list is only as fresh as its oldest constituent request.
    requestedAt: responses.length
      ? Math.min(
          ...responses.map((response) => response.data?.requestedAt ?? 0),
        )
      : 0,
    isLoading: responses.some((response) => response.isLoading),
    isPending: responses.some((response) => response.isPending),
    isFetching: responses.some((response) => response.isFetching),
    isFetched: responses.every((response) => response.isFetched),
    isError: responses.some((response) => response.isError),
    isSuccess: responses.every((response) => response.isSuccess),
  }
}

export const useChatSessions = (args: UseChatSessionsArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  const combineFn = useCallback(
    (responses: UseQueryResult<FetchedChatSessionList, Error>[]) => {
      const combined = combineChatSessionListResponses(responses)
      return {
        ...combined,
        data: args.select ? args.select(combined.data) : combined.data,
        count: combined.data.length,
        has_more: false,
      }
    },
    [args.select],
  )

  const queries = args.queryKeys.map((queryKey) => ({
    queryKey: chatSessionListQueryKeys.agent(queryKey),
    queryFn: async () => {
      const requestedAt = Date.now()
      const data = await fetchChatSessionList(queryKey, accessToken)
      const sessions = data.sessions.map((session) =>
        mergeCachedChatSessionListItemFields(queryClient, session),
      )

      // Cache individual sessions
      for (const session of sessions) {
        queryClient.setQueryData(
          simpleChatSessionQueryKeys.session({ sessionId: session.id }),
          session,
        )
      }

      return {
        ...data,
        sessions,
        requestedAt,
      }
    },
    staleTime: args.staleTime ?? Infinity,
  }))

  return useQueries({ queries, combine: combineFn })
}

// endregion: User Chats

// region: Bulk Operations

interface DeleteChatSessionsVariables {
  sessions: ChatSessionListItem[]
}

export const useDeleteChatSessions = () => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessions }: DeleteChatSessionsVariables) =>
      bulkDeleteChatSessions(
        { ids: sessions.map((session) => session.id) },
        accessToken,
      ),
    onSuccess: (_data, { sessions }) =>
      invalidateChatSessionListQueries(
        queryClient,
        sessions.flatMap<ChatSessionListQueryKey>(
          chatSessionListQueryKeys.forSession,
        ),
      ),
  })
}

const bulkDeleteChatSessions = async (
  req: BulkChatSessionsDelete,
  accessToken: string,
) => {
  if (req.ids.length === 0) {
    return
  }

  const url = `${API_URL}/api/v1/chat/sessions/bulk`

  const res: Response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
}

// endregion: Bulk Operations

// region: Duplication Operations

interface DuplicateChatSessionArgs {
  sessionId: UUID
}

export const useDuplicateChatSession = () => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: DuplicateChatSessionArgs) =>
      duplicateChatSession(args.sessionId, accessToken),
    onSuccess: (data) => {
      queryClient.setQueryData(
        simpleChatSessionQueryKeys.session({ sessionId: data.id }),
        data,
      )

      const listQueryKeys = chatSessionListQueryKeys.forSession(data)
      for (const queryKey of listQueryKeys) {
        queryClient.setQueryData<ChatSessionListResponse>(
          queryKey,
          produce((draft) => {
            if (
              !draft ||
              draft.sessions.some((session) => session.id === data.id)
            ) {
              return
            }
            draft.sessions.unshift(data)
            draft.count += 1
          }),
        )
      }

      void invalidateChatSessionListQueries(queryClient, listQueryKeys)
    },
  })
}

const duplicateChatSession = async (
  sessionId: UUID,
  accessToken: string,
): Promise<ChatSessionListItem> => {
  const url = `${API_URL}/api/v1/chat/sessions/${sessionId}/duplicate`

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListItem
}

// endregion: Duplication Operations

// region: Utils

interface AppendChatSessionToQueryDataArgs {
  queryClient: QueryClient
  data:
    | OrganizationChatSession
    | OpportunityChatSession
    | PursuitChatSession
    | PursuitDocumentChatSession
  key: QueryKey
}

const TEMPORARY_CHAT_TITLE_PREVIEW_MAX_LENGTH = 72

const normalizeTemporaryChatTitlePreview = (value: string) => {
  return value.replace(/\s+/g, ' ').trim()
}

const truncateTemporaryChatTitlePreview = (value: string) => {
  if (value.length <= TEMPORARY_CHAT_TITLE_PREVIEW_MAX_LENGTH) {
    return value
  }

  return `${value.slice(0, TEMPORARY_CHAT_TITLE_PREVIEW_MAX_LENGTH - 1).trimEnd()}…`
}

export const buildTemporaryChatTitlePreviewFromTextFragments = (
  textFragments: readonly string[],
  hasNonTextContent = false,
): string | null => {
  const textContent = textFragments
    .flatMap((fragment) => {
      const normalized = normalizeTemporaryChatTitlePreview(fragment)
      return normalized ? [normalized] : []
    })
    .join(' ')

  if (textContent) {
    return truncateTemporaryChatTitlePreview(textContent)
  }

  return hasNonTextContent ? 'Attachment' : null
}

const extractTemporaryChatTitlePreviewFromUserContent = (
  content: string | UserContent[],
): string | null => {
  if (typeof content === 'string') {
    return buildTemporaryChatTitlePreviewFromTextFragments([content])
  }

  return buildTemporaryChatTitlePreviewFromTextFragments(
    content.flatMap((part) => (typeof part === 'string' ? [part] : [])),
    content.length > 0,
  )
}

const buildTemporaryChatTitlePreview = (
  session: FetchableChatSession,
): string | null => {
  if (session.title) {
    return null
  }

  for (const chatMessage of session.all_messages) {
    if (chatMessage.message.kind !== 'request') {
      continue
    }

    for (const part of chatMessage.message.parts) {
      if (part.part_kind !== 'user-prompt' || isSystemReminderPart(part)) {
        continue
      }

      const preview = extractTemporaryChatTitlePreviewFromUserContent(
        part.content,
      )
      if (preview) {
        return preview
      }
    }
  }

  return null
}

/**
 * Lazily built index over every live session-list cache, keyed by session
 * id. `findChatSessionInListCaches` runs per render while a simple-session
 * query resolves its placeholder, and once per session inside the list-merge
 * loop, so rescanning every list linearly per call is O(sessions²) on a list
 * refetch and a per-render tax on every chat switch. The index makes each
 * call one Map hit; any write to a list key drops it, and the next lookup
 * rebuilds from the caches of that moment.
 */
interface ChatSessionListLookupCache {
  index: Map<string, ChatSessionListItem> | null
}

const chatSessionListLookupCaches = new WeakMap<
  QueryClient,
  ChatSessionListLookupCache
>()

const getChatSessionListIndex = (
  queryClient: QueryClient,
): Map<string, ChatSessionListItem> => {
  let cache = chatSessionListLookupCaches.get(queryClient)
  if (!cache) {
    const created: ChatSessionListLookupCache = { index: null }
    const listKeyRoot = chatSessionListQueryKeys.all()[0]
    // Never unsubscribed: the subscription lives exactly as long as the
    // cache it watches (one QueryClient per app; per-test clients take
    // their subscription down with them).
    queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === listKeyRoot) {
        created.index = null
      }
    })
    chatSessionListLookupCaches.set(queryClient, created)
    cache = created
  }
  if (!cache.index) {
    const index = new Map<string, ChatSessionListItem>()
    for (const [, cached] of queryClient.getQueriesData<{
      sessions?: ChatSessionListItem[]
    }>({ queryKey: chatSessionListQueryKeys.all() })) {
      for (const session of cached?.sessions ?? []) {
        // The first list holding the session wins, like the scan this
        // replaces.
        if (!index.has(session.id)) {
          index.set(session.id, session)
        }
      }
    }
    cache.index = index
  }
  return cache.index
}

/**
 * The session's row in whichever live session-list cache holds it. The list
 * queries stay observed for the whole layout lifetime, so this survives the
 * gcTime eviction that removes observer-less per-session seeds — it is the
 * durable fallback for client-derived fields (temporary_title_preview) and
 * fields the simple endpoint may omit.
 */
const findChatSessionInListCaches = (
  queryClient: QueryClient,
  sessionId: UUID | '',
): ChatSessionListItem | undefined => {
  if (!sessionId) {
    return undefined
  }
  return getChatSessionListIndex(queryClient).get(sessionId)
}

const mergeCachedChatSessionListItemFields = (
  queryClient: QueryClient,
  session: ChatSessionListItem,
): ChatSessionListItem => {
  const cached =
    queryClient.getQueryData<ChatSessionListItem>(
      simpleChatSessionQueryKeys.session({ sessionId: session.id }),
    ) ?? findChatSessionInListCaches(queryClient, session.id)

  if (session.title) {
    return {
      ...session,
      current_user_access:
        session.current_user_access ?? cached?.current_user_access,
      shared_with_user_grants:
        session.shared_with_user_grants ?? cached?.shared_with_user_grants,
      pinned_at:
        session.pinned_at === undefined
          ? (cached?.pinned_at ?? null)
          : session.pinned_at,
      temporary_title_preview: null,
    }
  }

  return {
    ...session,
    current_user_access:
      session.current_user_access ?? cached?.current_user_access,
    shared_with_user_grants:
      session.shared_with_user_grants ?? cached?.shared_with_user_grants,
    pinned_at:
      session.pinned_at === undefined
        ? (cached?.pinned_at ?? null)
        : session.pinned_at,
    temporary_title_preview:
      session.temporary_title_preview ??
      cached?.temporary_title_preview ??
      null,
  }
}

export const getChatSessionTemporaryTitlePreview = (
  session: ChatSessionListItem,
): string | null => {
  if (session.title) {
    return null
  }

  return session.temporary_title_preview ?? null
}

export const seedTemporaryChatSessionTitlePreview = (
  queryClient: QueryClient,
  args: {
    sessionId: UUID
    preview: string | null
    queryKeys: ChatSessionListQueryKeyArgs[]
  },
) => {
  if (!args.preview) {
    return
  }

  queryClient.setQueryData<ChatSessionListItem>(
    simpleChatSessionQueryKeys.session({ sessionId: args.sessionId }),
    (current) => {
      if (!current || current.title) {
        return current
      }

      return {
        ...current,
        temporary_title_preview: args.preview,
      }
    },
  )

  // forScope includes the aggregate all-pursuits list, so the preview also
  // reaches the pursuit folders in the org sidebar.
  for (const queryKey of args.queryKeys) {
    for (const listQueryKey of chatSessionListQueryKeys.forScope(queryKey)) {
      queryClient.setQueryData<ChatSessionListResponse>(
        listQueryKey,
        produce((draft) => {
          if (!draft) {
            return undefined
          }

          const session = draft.sessions.find(
            (current) => current.id === args.sessionId,
          )

          if (!session || session.title) {
            return
          }

          session.temporary_title_preview = args.preview
        }),
      )
    }
  }
}

const appendChatSessionToQueryData = (
  args: AppendChatSessionToQueryDataArgs,
) => {
  const { queryClient, data, key } = args
  queryClient.setQueryData<ChatSessionListResponse>(
    key,
    produce((draft) => {
      const newSession = chatSessionToListItem(data)

      if (!draft) {
        // don't update the query data if the query data hasn't been fetched yet
        return undefined
      }

      draft.sessions.push(newSession)
      draft.count += 1
    }),
  )
}

const chatSessionToListItem = (
  data:
    | OrganizationChatSession
    | OpportunityChatSession
    | PursuitChatSession
    | PursuitDocumentChatSession,
): ChatSessionListItem => {
  const sharedUserIds = getSharedUserIds(data)

  const base = {
    id: data.id,
    created_at: data.created_at,
    created_by: data.created_by_user_id,
    last_modified: data.last_modified,
    shared_user_ids: sharedUserIds,
    title: data.title,
    temporary_title_preview: buildTemporaryChatTitlePreview(data),
  }

  switch (data.initial_agent_name) {
    case 'organization_chat':
      return {
        ...base,
        initial_agent_name: 'organization_chat',
        organization_id: data.organization_id,
      }
    case 'opportunity_chat':
      return {
        ...base,
        initial_agent_name: 'opportunity_chat',
        organization_id: data.organization_id,
      }
    case 'pursuit_chat':
      return {
        ...base,
        initial_agent_name: 'pursuit_chat',
        organization_id: data.organization_id,
        pursuit_id: data.pursuit_id,
      }
    case 'pursuit_document_chat':
      return {
        ...base,
        initial_agent_name: 'pursuit_document_chat',
        organization_id: data.organization_id,
        pursuit_id: data.pursuit_id,
        document_id: data.document_id,
      }
    default: {
      const exhaustiveCheck: never = data
      return exhaustiveCheck
    }
  }
}

type ListedStandardChatSession =
  | OrganizationChatSession
  | OpportunityChatSession
  | PursuitChatSession
  | PursuitDocumentChatSession

const upsertChatSessionListItemInQueryData = (
  queryClient: QueryClient,
  key: QueryKey,
  item: ChatSessionListItem,
) => {
  queryClient.setQueryData<ChatSessionListResponse>(
    key,
    produce((draft) => {
      if (!draft) {
        return undefined
      }

      const existingIndex = draft.sessions.findIndex(
        (session) => session.id === item.id,
      )

      if (existingIndex === -1) {
        draft.sessions.unshift(item)
        draft.count += 1
        return
      }

      draft.sessions[existingIndex] = item
    }),
  )
}

export const syncChatSessionListItemCaches = (
  queryClient: QueryClient,
  data: ListedStandardChatSession,
) => {
  const item = mergeCachedChatSessionListItemFields(
    queryClient,
    chatSessionToListItem(data),
  )

  queryClient.setQueryData(
    simpleChatSessionQueryKeys.session({ sessionId: data.id }),
    item,
  )

  // forSession includes the aggregate all-pursuits list for pursuit
  // sessions, so the upsert reaches every list that holds the session.
  for (const queryKey of chatSessionListQueryKeys.forSession(item)) {
    upsertChatSessionListItemInQueryData(queryClient, queryKey, item)
  }
}

const invalidateKeysOnSuccess = (
  args: { invalidateKeysOnSuccess?: QueryKey[] },
  queryClient: QueryClient,
): Promise<void> | undefined => {
  if (args.invalidateKeysOnSuccess) {
    for (const [index, key] of args.invalidateKeysOnSuccess.entries()) {
      const promise = queryClient.invalidateQueries({ queryKey: key })
      if (index === args.invalidateKeysOnSuccess.length - 1) {
        return promise
      }
    }
  }
}

// endregion: Utils
